import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AnalystTubeCountPayload,
  BidDetailsReport,
  DashboardInitialState, ExpenseNotificationsData, FOUser, GetDashboardPayload, GetDashboardResult, MorningPdfType, MorningReportData,
  NotificationsData,
  SyncDownReportBlock,
} from '../utils/TypeDashboard';
import { ALL_TYPE, DEFAULT_FROM_DATE, DEFAULT_TO_DATE } from '../utils/Constant';

const initialState: DashboardInitialState = {
  isLoading: true,
  dashboardData: {} as GetDashboardResult,
  errorMessage: '',
  type: ALL_TYPE,
  fromDate: DEFAULT_FROM_DATE,
  toDate: DEFAULT_TO_DATE,
  queryParameter: {} as GetDashboardPayload,
  isMorningRepLoading: false,
  isAnalystTubeRepLoading: false,
  morningReportData: {} as { RecordsForDue: MorningReportData[], RecordsForMorningReport: MorningReportData[] },
  syncDownReportData: [] as SyncDownReportBlock[],
  isExpNotificationsLoading: false,
  isClearNotificationLoading: false,
  isClearSuccess: false,
  expenseNotifications: {} as ExpenseNotificationsData,
  notifications: {} as NotificationsData,
  foNotifications: {} as NotificationsData,
  isNotificationsLoading: false,
  isFoNotificationsLoading: false,
  foUsersList: [],
  bidReportList: [],
  isMorningRepPdfLoading: false,
  morningReportPdfData: {} as MorningPdfType,
  isReportRemoved: false,
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    resetDashboardState() {
      return initialState;
    },
    getDashboardData(state, action: PayloadAction<GetDashboardPayload>) {
      return {
        ...state,
        isLoading: true,
        queryParameter: action.payload,
      };
    },
    getDashboardDataSuccess(state, action: PayloadAction<GetDashboardResult>) {
      return {
        ...state,
        isLoading: false,
        dashboardData: action.payload,
      };
    },
    setQueryParameters(state, action: PayloadAction<GetDashboardPayload>) {
      return {
        ...state,
        isLoading: false,
        type: action.payload.type,
        fromDate: action.payload.fromDate,
        toDate: action.payload.toDate,
      };
    },
    getDashboardDataFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    },
    getMorningReport(state) {
      return {
        ...state,
        isMorningRepLoading: true,
      };
    },
    getMorningReportSuccess(state, action: PayloadAction<{ RecordsForDue: MorningReportData[], RecordsForMorningReport: MorningReportData[] }>) {
      return {
        ...state,
        isMorningRepLoading: false,
        morningReportData: action.payload,
      };
    },
    getMorningReportFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isMorningRepLoading: false,
        errorMessage: action.payload,
      };
    },
    getMorningReportPdf(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isMorningRepPdfLoading: true,
      };
    },
    getMorningReportPdfSuccess(state, action: PayloadAction<MorningPdfType>) {
      return {
        ...state,
        isMorningRepPdfLoading: false,
        morningReportPdfData: action.payload,
      };
    },
    getMorningReportPdfFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isMorningRepPdfLoading: false,
        errorMessage: action.payload,
      };
    },
    removeMorningReport(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isMorningRepPdfLoading: true,
        isReportRemoved: false,
      };
    },
    removeMorningReportSuccess(state) {
      return {
        ...state,
        isMorningRepPdfLoading: false,
        isReportRemoved: true,
      };
    },
    removeMorningReportFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isMorningRepPdfLoading: false,
        errorMessage: action.payload,
      };
    },
    clearIsReportRemoved(state) {
      return {
        ...state,
        isReportRemoved: false,
      };
    },
    clearMorningReportPdf(state) {
      return {
        ...state,
        morningReportPdfData: {} as MorningPdfType,
      };
    },
    getAnalystTubeCountReport(state, _action: PayloadAction<{ pay: AnalystTubeCountPayload, callback: () => void }>) {
      return {
        ...state,
        isAnalystTubeRepLoading: true,
      };
    },
    getAnalystTubeCountReportSuccess(state) {
      return {
        ...state,
        isAnalystTubeRepLoading: false,
      };
    },
    getAnalystTubeCountReportFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isAnalystTubeRepLoading: false,
        errorMessage: action.payload,
      };
    },
    getSyncDownReport(state) {
      return {
        ...state,
        isLoading: true,
      };
    },
    getSyncDownReportSuccess(state, action: PayloadAction<SyncDownReportBlock[]>) {
      return {
        ...state,
        isLoading: false,
        syncDownReportData: action.payload,
      };
    },
    getSyncDownReportFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    },
    getFOUsersList(state) {
      return {
        ...state,
        isLoading: true,
      };
    },
    getFOUsersListSuccess(state, action: PayloadAction<FOUser[]>) {
      return {
        ...state,
        isLoading: false,
        foUsersList: action.payload,
      };
    },
    getFOUsersListFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    },
    getBidReportList(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isLoading: true,
        bidReportList: [],
      };
    },
    getBidReportListSuccess(state, action: PayloadAction<BidDetailsReport[]>) {
      return {
        ...state,
        isLoading: false,
        bidReportList: action.payload,
      };
    },
    getBidReportListFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    },
    getExpenseNotifications(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isExpNotificationsLoading: true,
      };
    },
    getExpenseNotificationsSuccess(state, action: PayloadAction<ExpenseNotificationsData>) {
      return {
        ...state,
        isExpNotificationsLoading: false,
        expenseNotifications: action.payload,
      };
    },
    getExpenseNotificationsFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isExpNotificationsLoading: false,
        errorMessage: action.payload,
      };
    },
    getNotifications(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isNotificationsLoading: true,
      };
    },
    getNotificationsSuccess(state, action: PayloadAction<NotificationsData>) {
      return {
        ...state,
        isNotificationsLoading: false,
        notifications: action.payload,
      };
    },
    getNotificationsFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isNotificationsLoading: false,
        errorMessage: action.payload,
      };
    },
    getFoNotifications(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isFoNotificationsLoading: true,
      };
    },
    getFoNotificationsSuccess(state, action: PayloadAction<NotificationsData>) {
      return {
        ...state,
        isFoNotificationsLoading: false,
        foNotifications: action.payload,
      };
    },
    getFoNotificationsFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isFoNotificationsLoading: false,
        errorMessage: action.payload,
      };
    },
    clearExpenseNotification(state, _action: PayloadAction<object>) {
      return {
        ...state,
        isClearNotificationLoading: true,
      };
    },
    clearExpenseNotificationSuccess(state) {
      return {
        ...state,
        isClearNotificationLoading: false,
        isClearSuccess: true,
      };
    },
    clearExpenseNotificationFailure(state, action: PayloadAction<string>) {
      return {
        ...state,
        isClearNotificationLoading: false,
        errorMessage: action.payload,
      };
    },
    resetClearSuccess(state) {
      return {
        ...state,
        isClearSuccess: false,
      };
    },
  },
});

export const {
  getDashboardData, getDashboardDataSuccess, getDashboardDataFailure, setQueryParameters,
  resetDashboardState, getMorningReport, getMorningReportSuccess, getMorningReportFailure,
  getAnalystTubeCountReport, getAnalystTubeCountReportSuccess, getAnalystTubeCountReportFailure,
  getSyncDownReport, getSyncDownReportSuccess, getSyncDownReportFailure,
  getExpenseNotifications, getExpenseNotificationsSuccess, getExpenseNotificationsFailure,
  clearExpenseNotification, clearExpenseNotificationSuccess, clearExpenseNotificationFailure, resetClearSuccess,
  getNotifications, getNotificationsSuccess, getNotificationsFailure,
  getFOUsersList, getFOUsersListSuccess, getFOUsersListFailure,
  getBidReportList, getBidReportListSuccess, getBidReportListFailure,
  getMorningReportPdf, getMorningReportPdfSuccess, getMorningReportPdfFailure, clearMorningReportPdf,
  removeMorningReport, removeMorningReportSuccess, removeMorningReportFailure, clearIsReportRemoved,
  getFoNotifications, getFoNotificationsSuccess, getFoNotificationsFailure,
} = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;

export type DashBoardActions =
  | ReturnType<typeof getDashboardData>
  | ReturnType<typeof getDashboardDataSuccess>
  | ReturnType<typeof setQueryParameters>
  | ReturnType<typeof resetDashboardState>
  | ReturnType<typeof getDashboardDataFailure>
  | ReturnType<typeof getMorningReport>
  | ReturnType<typeof getMorningReportSuccess>
  | ReturnType<typeof getMorningReportFailure>
  | ReturnType<typeof getAnalystTubeCountReport>
  | ReturnType<typeof getAnalystTubeCountReportSuccess>
  | ReturnType<typeof getAnalystTubeCountReportFailure>
  | ReturnType<typeof getSyncDownReport>
  | ReturnType<typeof getSyncDownReportSuccess>
  | ReturnType<typeof getSyncDownReportFailure>
  | ReturnType<typeof getExpenseNotifications>
  | ReturnType<typeof getExpenseNotificationsSuccess>
  | ReturnType<typeof getExpenseNotificationsFailure>
  | ReturnType<typeof clearExpenseNotification>
  | ReturnType<typeof clearExpenseNotificationSuccess>
  | ReturnType<typeof clearExpenseNotificationFailure>
  | ReturnType<typeof resetClearSuccess>
  | ReturnType<typeof getNotifications>
  | ReturnType<typeof getNotificationsSuccess>
  | ReturnType<typeof getNotificationsFailure>
  | ReturnType<typeof getFOUsersList>
  | ReturnType<typeof getFOUsersListSuccess>
  | ReturnType<typeof getFOUsersListFailure>
  | ReturnType<typeof getBidReportList>
  | ReturnType<typeof getBidReportListSuccess>
  | ReturnType<typeof getBidReportListFailure>
  | ReturnType<typeof getMorningReportPdf>
  | ReturnType<typeof getMorningReportPdfSuccess>
  | ReturnType<typeof getMorningReportPdfFailure>
  | ReturnType<typeof clearMorningReportPdf>
  | ReturnType<typeof removeMorningReport>
  | ReturnType<typeof removeMorningReportSuccess>
  | ReturnType<typeof removeMorningReportFailure>
  | ReturnType<typeof clearIsReportRemoved>
  | ReturnType<typeof getFoNotifications>
  | ReturnType<typeof getFoNotificationsSuccess>
  | ReturnType<typeof getFoNotificationsFailure>;
